/**
 * ADMIN MODULE — Agents
 * Our own view of Paperclip agents in BrandCommand's design language (Paperclip's
 * own UI is never embedded). Lists agents with status + last heartbeat + current
 * issue, shows recent runs, and lets staff open a new Paperclip issue.
 * Auto-refreshes every 30s. All Paperclip calls go through paperclipClient.
 */

import { useCallback, useEffect, useState } from "react";
import { getAgents, getAgentRuns, createIssue, startPaperclipServer } from "../utils/paperclipClient.js";
import AdminLayout from "../components/AdminLayout.jsx";

const REFRESH_MS = 30000;

function fmtTime(t) {
  if (!t) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

// Paperclip statuses: idle/ready/active = up & available (green); running/busy/
// working = actively working (amber); anything else (offline/error/stopped) = red.
function statusClass(s) {
  if (["online", "idle", "ready", "active"].includes(s)) return "online";
  if (["running", "busy", "working"].includes(s)) return "running";
  return "offline";
}

// Which team an agent belongs to. Derived from its instructions path (robust to
// renames) with a role fallback for the CEO. Order here is the display order.
const GROUP_ORDER = ["CEO", "SEO", "LinkedIn Flywheel", "Other"];
function groupOf(agent) {
  if ((agent.role || "").toLowerCase() === "ceo") return "CEO";
  const p = (agent.adapterConfig?.instructionsFilePath || "").toLowerCase();
  if (p.includes("seo-rank")) return "SEO";
  if (p.includes("linkedin-flywheel")) return "LinkedIn Flywheel";
  return "Other";
}

function groupAgents(agents) {
  const buckets = {};
  for (const a of agents) (buckets[groupOf(a)] ||= []).push(a);
  return GROUP_ORDER.filter((g) => buckets[g]?.length).map((g) => [g, buckets[g]]);
}

const agentGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: 14,
};
const groupHeadingStyle = {
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: ".05em",
  textTransform: "uppercase",
  color: "var(--muted)",
  margin: "26px 0 10px",
};

function NewIssueModal({ agents, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [agentId, setAgentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) { setErr("A title is required."); return; }
    setBusy(true); setErr(null);
    const { error } = await createIssue(title.trim(), description.trim(), agentId || undefined);
    setBusy(false);
    if (error) { setErr(error); return; }
    onCreated();
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">New issue</h3>
        <p className="modal-message">Create a Paperclip issue and optionally assign it to an agent.</p>
        <form onSubmit={submit} style={{ marginTop: 16 }}>
          <label className="admin-field"><span>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus maxLength={200} />
          </label>
          <label className="admin-field"><span>Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="admin-field"><span>Assign to (optional)</span>
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">— Unassigned —</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          </label>
          {err && <p style={{ color: "var(--error)", fontSize: 13, margin: "0 0 8px" }}>{err}</p>}
          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            <button className="btn" type="submit" disabled={busy}>{busy ? "Creating…" : "Create issue"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AgentCard({ agent }) {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await getAgentRuns(agent.id);
      if (!alive) return;
      setRuns(error || !Array.isArray(data) ? [] : data.slice(0, 3));
    })();
    return () => { alive = false; };
  }, [agent.id]);

  return (
    <div className="admin-card admin-agent">
      <div className="admin-row-top">
        <span className="admin-row-title">{agent.name || agent.id}</span>
        <span className="admin-row-meta">
          <span className={`admin-dot ${statusClass(agent.status)}`} />
          {agent.status || "offline"}
        </span>
      </div>
      <div className="admin-row-meta">
        <span>Last heartbeat: {fmtTime(agent.lastHeartbeat || agent.last_heartbeat)}</span>
        <span>Issue: {agent.currentIssue?.title || agent.current_issue || "none"}</span>
      </div>
      <div className="admin-runs">
        {runs == null && <span className="admin-row-meta">Loading runs…</span>}
        {runs && runs.length === 0 && <span className="admin-row-meta">No recent runs.</span>}
        {runs && runs.map((r, i) => {
          const outcome = (r.outcome || r.status || "").toLowerCase();
          const ok = outcome === "success" || outcome === "completed";
          return (
            <div className="admin-run" key={r.id || i}>
              <span>{fmtTime(r.finishedAt || r.finished_at || r.createdAt || r.created_at)}</span>
              <span className={`admin-run-outcome ${ok ? "success" : outcome ? "failed" : ""}`}>
                {r.outcome || r.status || "—"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Offline({ onStart, starting, startErr }) {
  const canStart = import.meta.env.DEV; // launcher endpoint only exists under `npm run dev`
  return (
    <div className="admin-empty">
      <h3>Paperclip not reachable</h3>
      <p>The Agents view talks to the local Paperclip server (<code>127.0.0.1:3100</code>). It's probably just not running.</p>
      {canStart && (
        <p style={{ margin: "14px 0" }}>
          <button className="btn" onClick={onStart} disabled={starting}>
            {starting ? "Starting Paperclip… (~20s)" : "Start Paperclip"}
          </button>
        </p>
      )}
      {startErr && <p style={{ color: "var(--error)", fontSize: 13 }}>{startErr}</p>}
      <p style={{ fontSize: 13 }}>
        This runs <code>npx paperclipai run</code> on this machine. You can also start
        it yourself in a terminal — it serves <code>127.0.0.1:3100</code>. Then refresh.
      </p>
    </div>
  );
}

export default function AgentsView() {
  const [agents, setAgents] = useState(null); // null = loading, [] = none/offline
  const [offline, setOffline] = useState(false);
  const [modal, setModal] = useState(false);
  const [tick, setTick] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState(null);

  // Probe Paperclip; returns whether it's reachable (so callers can poll).
  const probe = useCallback(async () => {
    const { data, error } = await getAgents();
    const ok = !(error || !Array.isArray(data));
    setOffline(!ok);
    setAgents(ok ? data : []);
    return ok;
  }, []);

  useEffect(() => { probe(); }, [probe, tick]);

  // Auto-refresh every 30s.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // Launch Paperclip via the dev server, then poll until it answers (it boots an
  // embedded Postgres first, so give it up to ~45s).
  async function handleStart() {
    setStarting(true); setStartErr(null);
    const { error } = await startPaperclipServer();
    if (error) { setStartErr(error); setStarting(false); return; }
    for (let i = 0; i < 15; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      if (await probe()) break;
    }
    setStarting(false);
  }

  return (
    <AdminLayout title="Agents" subtitle="Paperclip agent orchestration · auto-refreshes every 30s.">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, gap: 10 }}>
        <button className="secondary" onClick={() => setTick((t) => t + 1)}>Refresh</button>
        <button className="btn" onClick={() => setModal(true)} disabled={offline}>New Issue</button>
      </div>

      {offline ? (
        <Offline onStart={handleStart} starting={starting} startErr={startErr} />
      ) : agents == null ? (
        <div className="admin-empty">Loading agents…</div>
      ) : agents.length === 0 ? (
        <div className="admin-empty"><h3>No agents yet</h3><p>Paperclip is connected but has no agents configured for this company.</p></div>
      ) : (
        groupAgents(agents).map(([group, list]) => (
          <section key={group}>
            <h2 style={groupHeadingStyle}>{group} · {list.length}</h2>
            <div style={agentGridStyle}>
              {list.map((a) => <AgentCard key={a.id} agent={a} />)}
            </div>
          </section>
        ))
      )}

      {modal && (
        <NewIssueModal
          agents={agents || []}
          onClose={() => setModal(false)}
          onCreated={() => setTick((t) => t + 1)}
        />
      )}
    </AdminLayout>
  );
}
